import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

import { sendOtp, verifyOtp } from "../../../services/auth";
import { type TVerifyOtpRequest } from "../../../services/auth/types";

import { OtpInput, OtpTimer } from "../components";
import { useOtpTimer } from "../hooks/useOtpTimer";
import { otpSchema, type OtpForm } from "../schema/auth.scehma";

interface OTPStepProps {
  phone: string;
  onSuccess: (data: TVerifyOtpRequest["response"]) => void;
}
const OTP_LENGTH = 6;

export const OtpStep = ({ phone, onSuccess }: OTPStepProps) => {
  const [otp, setOtp] = useState("");
  const { remainingTime, resetTimer } = useOtpTimer();

  const { handleSubmit } = useForm<OtpForm>({
    resolver: zodResolver(otpSchema),
    mode: "onChange",
    values: {
      otp,
    },
  });

  const { mutate: verifyUserOtp, isPending: isVerifyOtpPending } = useMutation<
    TVerifyOtpRequest["response"],
    Error,
    TVerifyOtpRequest["payload"]
  >({
    mutationFn: verifyOtp,
  });

  const { mutate: resendOtp, isPending: isResendOtpPending } = useMutation({
    mutationFn: sendOtp,
  });

  /*
   * Verify OTP
   */
  const onSubmit = () => {
    if (otp.length !== OTP_LENGTH) {
      return;
    }

    verifyUserOtp(
      {
        phoneNumber: phone,
        otp,
      },
      {
        onSuccess: (data) => {
          toast.success("کد تایید با موفقیت تایید شد 🎉");
          onSuccess(data);
        },
        onError: () => {
          toast.error("کد وارد شده اشتباه یا نامعتبر است ❌");
        },
      },
    );
  };

  /*
   * Resend OTP
   */
  const handleResend = () => {
    if (remainingTime > 0 || isResendOtpPending) {
      return;
    }

    resendOtp(
      {
        phoneNumber: phone,
      },
      {
        onSuccess: () => {
          setOtp("");
          resetTimer();

          toast.success("کد تایید مجدداً ارسال شد");
        },
        onError: () => {
          toast.error("ارسال مجدد کد با خطا مواجه شد");
        },
      },
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4.75">
      <OtpInput
        value={otp}
        onChange={setOtp}
        length={OTP_LENGTH}
        disabled={isVerifyOtpPending || isResendOtpPending}
      />

      <button
        type="submit"
        disabled={isVerifyOtpPending || isResendOtpPending}
        className="
          h-15.5
          w-full
          rounded-xl
          bg-linear-to-l
          from-[#4dbcf0]
          to-[#9b5cff]
          font-kalameh
          text-button-1
          text-white
          transition
          hover:opacity-90
          disabled:cursor-not-allowed
          disabled:opacity-60
        "
      >
        {isVerifyOtpPending ? (
          <p>در حال بررسی...</p>
        ) : (
          <div className="flex items-center justify-center gap-3">
            <p>ادامه</p>

            <img
              src="/images/arrow_right.png"
              alt="arrow"
              className="h-5.75 w-5.75"
            />
          </div>
        )}
      </button>
      <OtpTimer
        remainingTime={remainingTime}
        isResending={isResendOtpPending}
        onResend={handleResend}
      />
    </form>
  );
};
